/**
 * src/pages/DesignSpecimen.jsx — Phase 1 design-direction specimen.
 *
 * Public, unauthenticated, reachable only by direct URL — not linked from
 * the app or the marketing site (see App.jsx and REDESIGN.md's Phase 1,
 * "ends with STOP"). Demonstrates the recommended "Stencil" direction end to
 * end (type scale, palette, buttons, forms, a booking summary, a consent
 * question pattern, a dashboard table, two portal theme variants) plus
 * lighter comparison strips for the two directions not recommended (Ledger,
 * Line Weight). Full writeup: docs/redesign/direction.md.
 *
 * This is throwaway/preview code, not Phase 2's real token system — plain
 * scoped CSS with hand-written custom properties, not the JS source of
 * truth REDESIGN.md's Theme architecture §2 asks for. That gets built once
 * a direction is actually approved.
 */
import { useState } from 'react';
import { useNoindex } from '../lib/useNoindex.js';
import './design-specimen.css';

/** Six-step fluid type scale, ratio ~1.25, per direction.md's Stencil "Type" section. */
const TYPE_SCALE = [
  { name: '−1 · small / meta', className: 'specimen-type-sample--small', sample: 'Deposit paid · Consent signed' },
  { name: '0 · body (18px floor on portal)', className: 'specimen-type-sample--body', sample: 'A signing link goes out the moment a booking is confirmed.' },
  { name: '1 · h3 / label', className: 'specimen-type-sample--h3', sample: 'Choose a time' },
  { name: '2 · h2 / section', className: 'specimen-type-sample--h2', sample: 'Today, 21 September' },
  { name: '3 · h1 / page', className: 'specimen-type-sample--h1', sample: 'Your studio, booked' },
  { name: '4 · display (marketing hero)', className: 'specimen-type-sample--display', sample: 'Run the needle, not the spreadsheet' },
];

const LIGHT_SWATCHES = [
  { name: 'Stencil paper', role: '--bg', hex: '#F0F0EC' },
  { name: 'Working surface', role: '--surface', hex: '#FAFAF8' },
  { name: 'Graphite', role: '--text', hex: '#1C1B19' },
  { name: 'Ash', role: '--text-muted', hex: '#6B6862' },
  { name: 'Transfer violet', role: '--accent', hex: '#5E3A73' },
  { name: 'Fresh ink', role: '--danger', hex: '#A23B33' },
];

const DARK_SWATCHES = [
  { name: 'Stencil paper (dark)', role: '--bg', hex: '#19171B' },
  { name: 'Working surface (dark)', role: '--surface', hex: '#221F24' },
  { name: 'Graphite (dark)', role: '--text', hex: '#F0EEEC' },
  { name: 'Ash (dark)', role: '--text-muted', hex: '#A39E9A' },
  { name: 'Transfer violet (dark)', role: '--accent', hex: '#A279BC' },
  { name: 'Fresh ink (dark)', role: '--danger', hex: '#C4645A' },
];

const LEDGER_SWATCHES = [
  { name: 'Graphite', hex: '#1B1E24' },
  { name: 'Ledger card', hex: '#24272F' },
  { name: 'Carbon', hex: '#8B8F99' },
  { name: 'Lightbox amber', hex: '#E8A33D' },
];

const LINE_WEIGHT_SWATCHES = [
  { name: 'Paper', hex: '#FAFAF8' },
  { name: 'Ink', hex: '#171511' },
  { name: 'Ash', hex: '#78746C' },
  { name: 'Signal red (reserved)', hex: '#B23A2E' },
];

export default function DesignSpecimen() {
  useNoindex();
  const [mode, setMode] = useState('light');
  const swatches = mode === 'light' ? LIGHT_SWATCHES : DARK_SWATCHES;

  return (
    // data-theme='tattoo-labs': since design-specimen.css's own hardcoded
    // per-mode palette was retired (see that file's header comment), this
    // page now renders from the real tokens injectThemeTokens() generates,
    // same as the live app — it needs a real data-theme to match them.
    <div className="specimen" data-theme="tattoo-labs" data-mode={mode}>
      <header className="specimen-header">
        <div>
          <p className="specimen-eyebrow">REDESIGN.md · Phase 1 specimen — not a production page</p>
          <h1 className="specimen-title">Stencil</h1>
          <p className="specimen-lead">
            Thermal transfer paper and the line before the line. Full direction writeup, the review
            against REDESIGN.md&rsquo;s banned-pattern list, and the recommendation reasoning live in{' '}
            <code>docs/redesign/direction.md</code> — this page is the rendered specimen, not the
            pitch.
          </p>
        </div>
        <button
          type="button"
          className="specimen-btn specimen-btn-secondary"
          onClick={() => setMode((m) => (m === 'light' ? 'dark' : 'light'))}
        >
          Show {mode === 'light' ? 'dark' : 'light'} mode
        </button>
      </header>

      {/* --- Type scale --------------------------------------------------- */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Type scale</h2>
        <p className="specimen-section-note">
          Display: the OS's serif (<code>ui-serif</code> — New York on Mac/iOS), italic for
          emphasis, used once per screen. Everything else: the OS's UI sans (
          <code>-apple-system</code>/Segoe UI/Roboto, whichever the visitor's device supplies),
          with <code>ui-monospace</code> for tabular figures. Six steps, ratio ~1.25 — exact
          clamp() values are illustrative here; Phase 2 finalises the real modular scale.
        </p>
        <div className="specimen-type-scale">
          {TYPE_SCALE.map((step) => (
            <div key={step.name} className="specimen-type-row">
              <span className="specimen-type-label">{step.name}</span>
              <span className={`specimen-type-sample ${step.className}`}>{step.sample}</span>
            </div>
          ))}
        </div>
      </section>

      {/* --- Palette -------------------------------------------------------- */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Palette — {mode} mode</h2>
        <p className="specimen-section-note">
          Toggle above shows the other mode. The <em>hue</em> (violet) identifies the brand in both
          — only its lightness shifts, per direction.md.
        </p>
        <div className="specimen-swatch-row">
          {swatches.map((s) => (
            <div key={s.name} className="specimen-swatch">
              <div className="specimen-swatch-color" style={{ background: s.hex }} />
              <div className="specimen-swatch-label">{s.name}</div>
              <div className="specimen-swatch-meta">
                {s.role} · {s.hex}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- Buttons ---------------------------------------------------------- */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Buttons</h2>
        <p className="specimen-section-note">
          Hover and focus states are live — tab to a button to see the real
          <code>:focus-visible</code> ring, not a screenshot of one.
        </p>
        <div className="specimen-row">
          <button type="button" className="specimen-btn specimen-btn-primary">
            Set up your studio
          </button>
          <button type="button" className="specimen-btn specimen-btn-secondary">
            Sign in
          </button>
          <button type="button" className="specimen-btn specimen-btn-primary" disabled>
            Continue (disabled)
          </button>
        </div>
      </section>

      {/* --- Form fields, including an error state ----------------------------- */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Form fields</h2>
        <div className="specimen-form-grid">
          <div className="specimen-field">
            <label htmlFor="specimen-name">Your full name</label>
            <input id="specimen-name" type="text" defaultValue="Maya Okafor" autoComplete="name" />
          </div>
          <div className="specimen-field">
            <label htmlFor="specimen-email">Email</label>
            <input
              id="specimen-email"
              type="email"
              defaultValue="not-an-email"
              aria-invalid="true"
              aria-errormessage="specimen-email-error"
              autoComplete="email"
            />
            <p id="specimen-email-error" className="specimen-field-error" role="alert">
              Enter a valid email address.
            </p>
          </div>
        </div>
      </section>

      {/* --- Booking summary --------------------------------------------------- */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Booking summary</h2>
        <div className="specimen-panel specimen-panel--working">
          <span className="specimen-reg-mark specimen-reg-mark--tl" aria-hidden="true" />
          <span className="specimen-reg-mark specimen-reg-mark--tr" aria-hidden="true" />
          <p className="specimen-step">Step 2 of 4 — Choose a time</p>
          <h3 className="specimen-panel-title">Sat 14 March</h3>
          <div className="specimen-slot-row">
            <span className="specimen-slot">10:00</span>
            <span className="specimen-slot specimen-slot--selected">11:30</span>
            <span className="specimen-slot">13:00</span>
          </div>
          <p className="specimen-section-note">
            Deposit: £50, non-refundable within 48 hours of the appointment.
          </p>
          <button type="button" className="specimen-btn specimen-btn-primary">
            Continue
          </button>
        </div>
      </section>

      {/* --- Consent question group --------------------------------------------
          Generic Phase 3 component pattern (fieldset/legend + large Yes/No
          radio pair), not a claim about what the real consent form asks
          today — ConsentSign.jsx is currently one scrollable text block plus
          a signature, with no discrete questions. See audit.md §8. */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Consent question group (component pattern)</h2>
        <p className="specimen-section-note">
          Illustrative only — the real consent form has no discrete questions today (audit.md §8).
          This demonstrates the fieldset/legend/radio-pair pattern REDESIGN.md&rsquo;s Accessibility
          standard calls for, ready for Phase 3&rsquo;s component library if the form ever grows one.
        </p>
        <fieldset className="specimen-fieldset">
          <legend>Do you have any known allergies to latex or adhesives?</legend>
          <div className="specimen-radio-pair">
            <label className="specimen-radio-option">
              <input type="radio" name="specimen-allergy" value="yes" />
              Yes
            </label>
            <label className="specimen-radio-option">
              <input type="radio" name="specimen-allergy" value="no" defaultChecked />
              No
            </label>
          </div>
        </fieldset>
      </section>

      {/* --- Dashboard table row ------------------------------------------------ */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Dashboard table</h2>
        <table className="specimen-table">
          <caption className="specimen-table-caption">Today&rsquo;s sessions, 21 September</caption>
          <thead>
            <tr>
              <th scope="col">Time</th>
              <th scope="col">Client</th>
              <th scope="col">Session</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">09:00</th>
              <td>Maya Okafor</td>
              <td>Fine line, session 2</td>
              <td>Deposit paid · Consent signed</td>
            </tr>
            <tr>
              <th scope="row">11:30</th>
              <td>Tom Reilly</td>
              <td>Blackwork consult</td>
              <td>Consent outstanding</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* --- Portal theme variants: base violet vs. a studio-chosen accent ------ */}
      <section className="specimen-section">
        <h2 className="specimen-section-title">Portal theme variants</h2>
        <p className="specimen-section-note">
          Same structure, type and radius — only <code>--accent</code> differs. Previews Theme
          architecture §6&rsquo;s "studio picks a safe accent within the theme" idea a phase early;
          the real 4–6 curated portal themes are Phase 2&rsquo;s job.
        </p>
        <div className="specimen-theme-variants">
          <div className="specimen-panel specimen-portal-preview" data-accent="violet">
            <p className="specimen-step">Maya&rsquo;s Studio — base Stencil</p>
            <h3 className="specimen-panel-title">Book your session</h3>
            <button type="button" className="specimen-btn specimen-btn-primary">
              Continue
            </button>
          </div>
          <div className="specimen-panel specimen-portal-preview" data-accent="green">
            <p className="specimen-step">Old Anchor Tattoo — studio accent</p>
            <h3 className="specimen-panel-title">Book your session</h3>
            <button type="button" className="specimen-btn specimen-btn-primary">
              Continue
            </button>
          </div>
        </div>
      </section>

      {/* --- Comparison strips: the two directions not recommended --------------- */}
      <section className="specimen-section specimen-section--comparison">
        <h2 className="specimen-section-title">For comparison — the other two directions</h2>
        <p className="specimen-section-note">
          Palette and a type sample only. Full writeups, wireframes and principles in
          docs/redesign/direction.md.
        </p>
        <div className="specimen-comparison-row">
          <div className="specimen-comparison-card">
            <h3 className="specimen-comparison-title">Ledger</h3>
            <p className="specimen-comparison-subtitle">The appointment book, ruled and lit from below.</p>
            <div className="specimen-swatch-row specimen-swatch-row--compact">
              {LEDGER_SWATCHES.map((s) => (
                <div key={s.name} className="specimen-swatch specimen-swatch--compact">
                  <div className="specimen-swatch-color" style={{ background: s.hex }} />
                  <div className="specimen-swatch-label">{s.name}</div>
                </div>
              ))}
            </div>
            <p className="specimen-comparison-sample">09:00 · Maya Okafor · Session 2</p>
          </div>
          <div className="specimen-comparison-card">
            <h3 className="specimen-comparison-title">Line Weight</h3>
            <p className="specimen-comparison-subtitle">Whip shading and negative space. The quietest of the three.</p>
            <div className="specimen-swatch-row specimen-swatch-row--compact">
              {LINE_WEIGHT_SWATCHES.map((s) => (
                <div key={s.name} className="specimen-swatch specimen-swatch--compact">
                  <div className="specimen-swatch-color" style={{ background: s.hex }} />
                  <div className="specimen-swatch-label">{s.name}</div>
                </div>
              ))}
            </div>
            <p className="specimen-comparison-sample specimen-comparison-sample--thin">
              Booking, deposits, consent. Nothing else.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
